import { createSlice } from '@reduxjs/toolkit';

// Define initial state
const INITIAL_ORDER = {
  order_no: null,
  customer: null,
  subtotal: 0,
  total: 0,
  type: 'Online Sale',
  sale_from: 'Website',
  paid_amount: 0,
  coupon_discount: 0,
  products_discount: 0,
  total_discount: 0,
  coupon_discount_type: 'Amount',
  coupon_discount_value: 0,
  coupon_code: '',
  profit_amount: 0,
  payment_status: 'Unpaid',
  due_amount: 0,
  status: 'To Pay',
  delivery_company: null,
  store: null,
  note: null,
  products: [], // Holds the product objects
  payments: [],
  statuses: {},
  timelines: [],
  activities: [],
  address: { phone: '', receiver: '', state: '', city: '', address: '' },
  max_discount_value: 0,
};

// Helper function for recalculating order totals
const recalculateOrder = (state) => {
  // Calculate subtotal, profit, and total
  state.subtotal = state.products.reduce((sum, product) => sum + product.subtotal, 0);
  state.profit_amount = state.products.reduce((sum, product) => sum + product.profit, 0);
  state.products_discount = state.products.reduce((sum, item) => sum + item.discount, 0);

  // Apply discount
  if (state.coupon_discount_type === 'Percentage') {
    state.coupon_discount = (parseFloat(state.coupon_discount_value) / 100) * state.subtotal;
  } else {
    state.coupon_discount = parseInt(state.coupon_discount_value, 10);
  }

  if (state.coupon_discount > parseInt(state.max_discount_value, 10)) {
    state.coupon_discount = parseInt(state.max_discount_value, 10);
  }
  state.total_discount = parseInt(state.products_discount, 10) + parseInt(state.coupon_discount, 10);

  // Calculate total and due amount
  state.total = state.subtotal - state.total_discount;
  state.due_amount = state.total - state.paid_amount;

  // Update payment status and pending task
  if (state.due_amount === state.total) {
    state.payment_status = 'Unpaid';
    state.status = 'To Pay';
  } else if (state.due_amount > 0) {
    state.payment_status = 'Partially Paid';
    state.status = 'To Pay';
  } else {
    state.payment_status = 'Paid';
    state.status = 'To Verify';
  }
};

// Create the slice
const orderSlice = createSlice({
  name: 'order',
  initialState: INITIAL_ORDER,
  reducers: {
    setOrder: (state, action) => {
      return { ...state, ...action.payload };
    },
    setProducts: (state, action) => {
      const products = action.payload;
      state.products = products;
      recalculateOrder(state);
    },
    setCoupon: (state, action) => {
      const { coupon_code, coupon_discount_type, coupon_discount_value, max_discount_value } = action.payload;
      state.coupon_code = coupon_code;
      state.coupon_discount_type = coupon_discount_type;
      state.coupon_discount_value = coupon_discount_value;
      state.max_discount_value = max_discount_value;
      recalculateOrder(state);
    },

    removeProduct: (state, action) => {
      const name = action.payload;
      state.products = state.items.filter((item) => item.name !== name);
      recalculateOrder(state);
    },
    updateProduct: (state, action) => {
      const { name, quantity } = action.payload; // `quantity` will be -1 or +1
      const existingProductIndex = state.products.findIndex((item) => item.name === name);

      if (existingProductIndex !== -1) {
        const existingProduct = state.products[existingProductIndex];

        // Adjust the product's quantity
        existingProduct.quantity += quantity;

        // Ensure quantity stays within valid bounds
        if (existingProduct.quantity > existingProduct.number_of_stock) {
          existingProduct.quantity = existingProduct.number_of_stock;
        }

        // If quantity drops to 0, remove the product from the array
        if (existingProduct.quantity <= 0) {
          state.products.splice(existingProductIndex, 1); // Remove product from array
        } else {
          // Recalculate product-specific values if not removed
          existingProduct.subtotal = existingProduct.price * existingProduct.quantity;
          existingProduct.profit = (existingProduct.price - existingProduct.cost_per_item) * existingProduct.quantity;
          existingProduct.margin =
            existingProduct.subtotal > 0 ? ((existingProduct.profit / existingProduct.subtotal) * 100).toFixed(2) : '0';
          existingProduct.discount =
            parseInt(existingProduct.sale_price, 10) > 0
              ? (parseInt(existingProduct.regular_price, 10) - parseInt(existingProduct.sale_price, 10)) *
                existingProduct.quantity
              : 0;
        }

        // Recalculate the entire order
        recalculateOrder(state);
      }
    },

    setDiscount: (state, action) => {
      const { type, value } = action.payload;
      if (type === 'Percentage') {
        state.discount_type = 'Percentage';
        state.discount_percentage = value;
      } else {
        state.discount_type = 'Amount';
        state.discount = value;
      }
      recalculateOrder(state);
    },
    resetOrder: () => INITIAL_ORDER,
  },
});

// Export actions and reducer
export const { setOrder, updateProduct, setDiscount, resetOrder, setProducts, removeProduct, setCoupon } =
  orderSlice.actions;

export default orderSlice.reducer;
